import { useEffect, useState } from 'react';

import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { QueryRefetchInterval } from '@cast/shared-types';
import { OptimizationWorkloadPod } from '@cast/types';
import { DatePeriodInTimestamp } from '@cast/design-system';

import { WorkloadQueryData } from 'hooks/queries/workload-optimization';

import { useRightsizingTimeRangeSelection } from './useRightsizingTimeRangeSelection';
import { useWoopWorkload } from './useWoopWorkload';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type TimeRangeSelection = {
  getRange: () => DatePeriodInTimestamp;
  preset?: string;
  timeZone: string;
  key: string;
};

type UseCurrentWorkloadMetricsArgs = {
  workloadId: string;
  refetchInterval?: QueryRefetchInterval<WorkloadQueryData>;
  includeMetrics?: boolean;
  includeCosts?: boolean;
  timeRangeSelection?: TimeRangeSelection;
};

// this removes unnecessary rerenders on updates
const EMPTY_PODS_VALUE: OptimizationWorkloadPod[] = [];

const getRangeWithPrediction = ({
  from,
  to,
  availableUntil,
}: {
  from?: string;
  to?: string;
  availableUntil: string;
}): { from?: string; to?: string } => {
  if (!from || !to) {
    return { from, to };
  }

  const fromDate = dayjs.utc(from);
  const toDate = dayjs.utc(to);
  const isToToday = toDate.isSame(dayjs().utc(), 'day');

  let finalToDate: dayjs.Dayjs;

  if (!isToToday) {
    // Do not apply prediction for past ranges
    finalToDate = toDate;
  } else {
    const duration = dayjs.duration(toDate.diff(fromDate));
    const threeDays = dayjs.duration(3, 'days');

    const toDateWithPrediction =
      duration.asMilliseconds() > threeDays.asMilliseconds()
        ? toDate.add(3, 'days')
        : toDate.add(duration);

    // Clamp to availableUntil if needed
    finalToDate = dayjs.utc(availableUntil).isBefore(toDateWithPrediction)
      ? dayjs.utc(availableUntil)
      : toDateWithPrediction;
  }

  return {
    from,
    to: finalToDate.toISOString(),
  };
};

export const useCurrentWorkloadMetrics = ({
  workloadId,
  refetchInterval = undefined,
  includeMetrics = true,
  includeCosts = true,
  timeRangeSelection,
}: UseCurrentWorkloadMetricsArgs) => {
  const { cluster } = useCluster();
  const rightsizingTimeRangeSelection = useRightsizingTimeRangeSelection();
  const { getRange, preset, timeZone, key } =
    timeRangeSelection ?? rightsizingTimeRangeSelection;

  const [availableUntil, setAvailableUntil] = useState<string | null>(null);

  const {
    workload,
    metrics,
    pods,
    loadedLength,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useWoopWorkload({
    clusterId: cluster.id,
    workloadId,
    includeMetrics,
    includeCosts,
    refetchInterval,
    selection: {
      periodLabel: preset,
      timezone: timeZone,
      range: availableUntil
        ? getRangeWithPrediction({
            ...getRange(),
            availableUntil,
          })
        : getRange(),
    },
    placeholderData: keepPreviousData,
    key: availableUntil ? `${key}-${availableUntil}` : key,
  });

  useEffect(() => {
    if (!workload?.predictionInsights.isEligible) {
      setAvailableUntil(null);
      return;
    }

    if (workload?.predictionInsights.availableUntil) {
      setAvailableUntil(workload.predictionInsights.availableUntil);
      return;
    }
  }, [
    workload?.predictionInsights.availableUntil,
    workload?.predictionInsights.isEligible,
  ]);
  return {
    workload,
    metrics,
    pods: pods ?? EMPTY_PODS_VALUE,
    metricsLoadedLength: loadedLength,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
